<template>
    <div class="flex flex-column relative">
        <listing-batch
            v-show="selection.length > 0"
            :listinghash="listinghash"
            :selection="selection"
            :enableAllPages="display.enableAllPages"
        />
        <el-table
            ref="table"
            height="100%"
            style="width: 100%"
            class="fixed-row-height"
            :class="listingClasses"
            :data="rows"
            :defaultSort="defaultSort"
            border
            @select="selectOneRow"
            @select-all="selectAllRow"
            @sort-change="sendOrder"
            @header-dragend="updateColumnWidth"
        >
            <!-- BATCH -->
            <el-table-column
                v-if="hasBatchAction"
                fixed
                type="selection"
                prop="Batch"
                label=""
                width="41"
            >
            </el-table-column>

            <!-- NAME -->
            <el-table-column
                v-if="mainColumn"
                :key="mainColumn.name"
                fixed
                :sortable="mainColumn.sortable === true ? `custom` : false"
                :sortMethod="sortColumn"
                :prop="mainColumn.name"
                :label="mainColumn.label"
                showOverflowTooltip
                :minWidth="convertedWidth(mainColumn.width, mainColumn.name)"
            >
                <template #default="scope">
                    <router-link
                        v-if="
                            scope.row['formatted_' + mainColumn.name] &&
                            typeof scope.row['formatted_' + mainColumn.name] !== 'string'
                        "
                        class="subcell semibold gray-100"
                        :to="
                            scope.row['formatted_' + mainColumn.name] !== undefined
                                ? scope.row['formatted_' + mainColumn.name].link
                                : scope.row[mainColumn.name].link
                        "
                    >
                        {{
                            scope.row['formatted_' + mainColumn.name] !== undefined
                                ? scope.row['formatted_' + mainColumn.name].input
                                : scope.row[mainColumn.name].input
                        }}
                    </router-link>
                    <div
                        v-else
                        class="subcell semibold"
                        v-html="
                            scope.row['formatted_' + mainColumn.name] !== undefined
                                ? scope.row['formatted_' + mainColumn.name]
                                : scope.row[mainColumn.name]
                        "
                    ></div>
                </template>
            </el-table-column>

            <!-- ALL COLUMNS -->
            <el-table-column
                v-for="header in displayedColumns"
                :key="`header-${header.name}-${header.rank}`"
                :prop="header.name"
                :label="header.label"
                :sortBy="header.name"
                showOverflowTooltip
                :sortable="header.sortable === true ? `custom` : false"
                :sortMethod="sortColumn"
                headerAlign="left"
                :align="header.alignment"
                resizable
                :minWidth="convertedWidth(header.width, header.name)"
            >
                <template #header="{ column }">
                    <CompassTooltip :content="column.label" placement="top">
                        {{ column.label }}
                    </CompassTooltip>
                </template>
                <template #default="scope">
                    <template
                        v-if="
                            scope.row['formatted_' + header.name] !== undefined &&
                            scope.row['formatted_' + header.name] !== null
                        "
                    >
                        <template
                            v-if="
                                typeof scope.row['formatted_' + header.name] === 'object' &&
                                scope.row['formatted_' + header.name].type === 'component'
                            "
                        >
                            <component
                                :is="scope.row['formatted_' + header.name].name"
                                v-bind="scope.row['formatted_' + header.name].params"
                                :key="`${header.name}_${scope.row.id}`"
                                :listinghash="listinghash"
                            />
                        </template>
                        <div
                            v-else
                            class="subcell"
                            v-html="scope.row['formatted_' + header.name]"
                        />
                    </template>
                    <template
                        v-else-if="isEmailEngineEnabled && header.name === 'lastEmailChaseDate'"
                    >
                        <CompassTooltip
                            :disabled="
                                !canDisplayTooltipOfLastEmailSentDate(scope.row[header.name])
                            "
                            :content="scope.row[header.name]"
                            :arrow="false"
                        >
                            {{ getLabelOfLastEmailSentDate(scope.row[header.name]) }}
                        </CompassTooltip>
                    </template>
                    <template v-else>{{ scope.row[header.name] }}</template>
                </template>
            </el-table-column>

            <!-- CONTEXT MENU -->
            <el-table-column
                v-if="hasContextMenu"
                fixed="right"
                label=""
                width="40"
                className="el-table-column--contextmenu"
            >
                <template #default="scope">
                    <listing-table-element-menu
                        :id="scope.row.id"
                        :items="scope.row.contextmenu.items"
                    />
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import _ from 'lodash';
import ListingBatch from './ListingBatch.vue';
import ListSelector from './ListSelector.vue';
import TagStatus from 'vcomponents/common/TagStatus/TagStatus.vue';
import CellCheckbox from './CellCheckbox.vue';
import PersonList from 'vcomponents/utilities/PersonList.vue';
import ChipLabel from 'vcomponents/utilities/ChipLabel.vue';
import ListingDropdown from 'vcomponents/listing/ListingDropdown.vue';
import EventValue from 'vcomponents/utilities/EventValue.vue';
import ObjectValue from 'vcomponents/utilities/ObjectValue.vue';
import DateValue from 'vcomponents/utilities/DateValue.vue';
import DateNoTimeValue from 'vcomponents/utilities/DateNoTimeValue.vue';
import ObjectLinkValue from 'vcomponents/utilities/ObjectLinkValue.vue';
import OcrItemListAction from 'vcomponents/ocr/OcrItemListAction.vue';
import OcrItemState from 'vcomponents/ocr/OcrItemState.vue';
import ContextMenuMixins from 'mixins/ContextMenuMixins';
import TagRelationTypeColumn from 'vcomponents/listing/tableColumns/TagRelationTypeColumn.vue';
import ListingTableElementMenu from 'vcomponents/listing/ListingTableElementMenu.vue';
import Feature from 'Services/Feature';

import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
import { defineAsyncComponent } from 'vue';

export default {
    components: {
        'person-list': PersonList,
        'date-value': DateValue,
        'date-no-time-value': DateNoTimeValue,
        'event-value': EventValue,
        'object-value': ObjectValue,
        'object-link-value': ObjectLinkValue,
        'ocr-item-list-action': OcrItemListAction,
        'ocr-item-state': OcrItemState,
        ListingBatch,
        ChipLabel,
        ListingDropdown,
        ListSelector,
        CellCheckbox,
        TagRelationTypeColumn,
        ListingTableElementMenu,
        TagStatus,
        ProgressInvoiceProgressBar: defineAsyncComponent(() =>
            import('vcomponents/listing/ProgressInvoiceProgressBar/ProgressInvoiceProgressBar.vue'),
        ),
    },
    mixins: [ContextMenuMixins],
    props: {
        listinghash: {
            type: String,
            required: true,
        },
        rows: {
            type: Array,
            default() {
                return [];
            },
        },
        columns: {
            type: Array,
            default() {
                return [];
            },
        },
    },

    data() {
        return {
            ordering: false,
            selection: [],
            selectAll: false,
            width: {
                XS: 48,
                S: 96,
                M: 144,
                L: 176,
                XL: 196,
                XXL: 240,
            },
        };
    },

    computed: {
        listingClasses() {
            let classes = this.tableDensity === 'condensed' ? 'table-condensed' : '';

            if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent))
                classes += ' table-ellipsis';

            return classes;
        },

        hasContextMenu() {
            return (
                this.rows.filter((row) => {
                    return row.contextmenu !== undefined;
                }).length > 0
            );
        },

        displayedColumns() {
            // When displaying columns, we make sure this.selection is according with table selection
            if (
                this.$refs.table &&
                this.selection.length !== this.$refs.table.getSelectionRows().length
            ) {
                this.selection = this.$refs.table.getSelectionRows();
            }
            return this.columns.filter((col) => !col.isMain);
        },

        mainColumn() {
            const columnIndex = _.findIndex(this.columns, { isMain: true });

            if (columnIndex !== -1) {
                return this.columns[columnIndex];
            }

            return false;
        },

        display() {
            return this.$store.state[this.listinghash].display;
        },

        sorting() {
            return this.$store.state[this.listinghash].sorting;
        },

        hasBatchAction() {
            return this.$store.state[this.listinghash].batchActions.length > 0;
        },

        tableDensity() {
            return this.$store.state[this.listinghash].display.density;
        },

        listingname() {
            return this.$store.state[this.listinghash].listingname;
        },

        columnsSize() {
            return this.$store.state[this.listinghash].columnsSize[this.listingname];
        },

        defaultSort() {
            const sorting = this.$store.state[this.listinghash].sorting;
            if (sorting.field) {
                return {
                    prop: sorting.field,
                    order: sorting.direction === 'asc' ? 'ascending' : 'descending',
                };
            }
            return { prop: '' };
        },
        isEmailEngineEnabled() {
            return Feature.isEnabled('email-engine');
        },
    },

    created() {
        window.EventBus.on('filters:apply', () => this.resetSelection());

        EventBus.on('batchaction:callback', () => {
            this.resetSelection();
            this.$store.commit(`${this.listinghash}/hideDisplay`, 'batchActions');
            this.$store.dispatch(`${this.listinghash}/fetch`);
        });

        EventBus.on('listing:updatePage', (payload) => {
            if (this.listinghash === payload.listing) {
                this.resetSelection();
            }
        });

        window.EventBus.on('listing:sortUpdated', (payload) => {
            this.updateSortCaret(payload.field, payload.direction + 'ending');
        });
    },

    methods: {
        ...popupPanelMapActions(['open']),
        resetSelection() {
            this.$refs.table.clearSelection();
            this.selection = [];
        },
        convertedWidth(value, name) {
            const column = this.columnsSize
                ? this.columnsSize.find((col) => col.name === name)
                : null;
            if (column) {
                return column.width.toString();
            }

            if (Object.keys(this.width).find((key) => key === value)) {
                return this.width[value].toString();
            }
            return value.toString();
        },

        sortBy(column) {
            const newSorting = {
                field: column,
                direction: 'asc',
            };

            if (this.sorting.field === column && this.sorting.direction === 'asc') {
                newSorting.direction = 'desc';
            }

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        },

        selectOneRow(selection) {
            this.selection = selection;
            this.selectAll = this.selection.length === this.rows.length ? true : false;
            this.$store.state[this.listinghash].display.visibleAllPages = this.selectAll;
            if (!this.selectAll) {
                this.$store.state[this.listinghash].display.checkAllPages = false;
            }
        },

        selectAllRow(selection) {
            this.selection = selection;
            this.selectAll = this.selection.length === this.rows.length ? true : false;
            this.$store.state[this.listinghash].display.visibleAllPages = this.selectAll;
        },

        sendOrder: _.debounce(function (column) {
            if (this.ordering) {
                this.ordering = false;
                return;
            }
            const newOrder = column.order === 'descending' ? 'desc' : 'asc';
            const newSorting = {
                field: column.prop,
                direction: newOrder,
            };

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        }, 300),

        updateSortCaret(column, direction) {
            if (!this.$refs.table) {
                return false;
            }
            this.$refs.table.clearSort();
            this.ordering = true;
            this.$refs.table.sort(column, direction);
        },

        updateColumnWidth(newWidth, oldWidth, column, event) {
            this.$store.dispatch(`${this.listinghash}/updateColumnsSettings`, {
                newWidth,
                oldWidth,
                column,
                event,
            });
        },

        sortColumn() {
            return 0;
        },
        getLabelOfLastEmailSentDate(lastEmailSentDate) {
            if (!!!lastEmailSentDate || lastEmailSentDate === 'N/C') {
                return lastEmailSentDate;
            }

            let labelOFLastEmailSentDate = lastEmailSentDate;
            let formatedLastEmailSentDate = lastEmailSentDate;

            //TODO remove this when we have a proper date format
            //This is a workaround for the date format issue in the listing table
            const splitLastEmailSentDate = lastEmailSentDate.split('/');
            if (splitLastEmailSentDate.length === 3) {
                // Convert to YYYY-MM-DD format for moment.js
                formatedLastEmailSentDate = `${splitLastEmailSentDate[2]}-${splitLastEmailSentDate[1]}-${splitLastEmailSentDate[0]}`;
            }

            if (moment(formatedLastEmailSentDate).isValid()) {
                const currentDate = moment();
                const lastEmailDate = moment(formatedLastEmailSentDate);
                const diffDays = currentDate.diff(lastEmailDate, 'days');
                const diffMonths = currentDate.diff(lastEmailDate, 'months');

                const maxDiffDays = 30;
                const maxDiffMonths = 12;
                const yesterdayDiffDays = 1;

                if (diffDays === 0) {
                    //TODO use the key "components.listing.ListingTableElement.last_sending_date.label.few_hours_ago" when we have proper date format
                    labelOFLastEmailSentDate = this.$t.__(
                        'components.listing.ListingTableElement.last_sending_date.label.today',
                    );
                } else if (diffDays === yesterdayDiffDays) {
                    labelOFLastEmailSentDate = this.$t.__(
                        'components.listing.ListingTableElement.last_sending_date.label.yesterday',
                    );
                } else if (diffDays > yesterdayDiffDays && diffDays < maxDiffDays) {
                    labelOFLastEmailSentDate = this.$t.__(
                        'components.listing.ListingTableElement.last_sending_date.label.few_days_ago',
                        { days: diffDays },
                    );
                } else if (diffDays >= maxDiffDays && diffMonths < maxDiffMonths) {
                    labelOFLastEmailSentDate = this.$t.__(
                        'components.listing.ListingTableElement.last_sending_date.label.few_months_ago',
                        { months: diffMonths },
                    );
                }
            }

            return labelOFLastEmailSentDate;
        },
        canDisplayTooltipOfLastEmailSentDate(lastEmailSentDate) {
            return lastEmailSentDate && lastEmailSentDate !== 'N/C';
        },
    },
};
</script>
